import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { PostOnboardDeviceLogsRequest, ToastType } from '../interfaces';
import { useToastMessage } from '../hooks/useToastMessage';
import { useOnboardDeviceLogsSync } from '../hooks/useOnboardDeviceLogsSync';
import { useStoredOnboardDeviceLogs } from '../hooks/useStoredOnboardDeviceLogs';
import { useOnboardDeviceLogsArchive } from '../hooks/useOnboardDeviceLogsArchive';
import ToastWrapper from './ToastWrapper';
import { SyncLogsForm } from './OnboardDeviceLogs/SyncLogsForm';
import { SyncStatusSummary } from './OnboardDeviceLogs/SyncStatusSummary';
import { SyncProgressBar } from './OnboardDeviceLogs/SyncProgressBar';
import { StoredLogsGrid } from './OnboardDeviceLogs/StoredLogsGrid';

interface Props {
    open: boolean;
    selectedDeviceId: string;
    onClose: () => void;
}

export const UploadOnboardDeviceLogsDialog = ({ open, onClose, selectedDeviceId }: Props) => {
    const [selectedLogIds, setSelectedLogIds] = useState<GridRowSelectionModel>([]);
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();

    const showError = (message: string) => setToastMessage({ message, type: ToastType.WARNING });

    const { storedLogs, storedLogsLoading, loadStoredLogs } = useStoredOnboardDeviceLogs(
        selectedDeviceId,
        showError,
    );

    const { progress, uploadStatus, uploadDetails, uploadInProgress, startSync } = useOnboardDeviceLogsSync({
        deviceId: selectedDeviceId,
        open,
        onLogsAvailable: loadStoredLogs,
    });

    const { downloading, downloadLogs } = useOnboardDeviceLogsArchive(selectedDeviceId, showError);

    useEffect(() => {
        if (open) {
            loadStoredLogs();
        }
    }, [open, loadStoredLogs]);

    const handleSync = (payload: PostOnboardDeviceLogsRequest) => {
        startSync(payload);
    };

    const handleDownloadSelected = () => {
        downloadLogs(selectedLogIds.map(String));
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Device Logs for Device {selectedDeviceId}</DialogTitle>
                <DialogContent>
                    <SyncLogsForm uploadInProgress={uploadInProgress} onSync={handleSync} />
                    <SyncStatusSummary status={uploadStatus} details={uploadDetails} />
                    <SyncProgressBar progress={progress} />
                    <StoredLogsGrid
                        logs={storedLogs}
                        loading={storedLogsLoading}
                        selectedLogIds={selectedLogIds}
                        onSelectionChange={setSelectedLogIds}
                        downloading={downloading}
                        onDownload={handleDownloadSelected}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </>
    );
};
